import { Author, Coordinate, GenericLayer, Layer, Project } from "@nova/protobuf"
import { ProtoType } from "../../serializer"
import {
    isValidJSON,
    isValidLatitude,
    isValidLongitude,
    isValidLonLat,
} from "@nova/calculate-geospatial"
import { ignoredProjectKeys, sampleDataIds } from "@nova/constants"

export type Defined<T> = T extends undefined ? never : T
export type ValidatorFN<T, K extends keyof T> = (
    val: Defined<T[K]>,
    obj: T,
    project: Project,
    path: readonly (string | object)[]
) => boolean
export type Validator<T> = {
    [K in keyof T]?: {
        validators: {
            validator: ValidatorFN<T, K>
            error: string
        }[]
    } & (undefined extends T[K] ? { nonNull?: true } : { nonNull: true })
}
export const entityValidator = <T, V extends Validator<ProtoType<T>>>(
    _: T,
    validation: { [K in keyof V]: K extends keyof ProtoType<T> ? V[K] : never }
) => validation

type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (
    k: infer I
) => void
    ? I
    : never

type LastOf<U> =
    UnionToIntersection<U extends unknown ? (x: U) => void : never> extends (x: infer I) => void
        ? I
        : never

export type UnionToTuple<U, T extends unknown[] = []> = [U] extends [never]
    ? T
    : UnionToTuple<Exclude<U, LastOf<U>>, [LastOf<U>, ...T]>

type SortableLayer = { parentId?: string; index?: string; id: string }

export const jsonValidators = [
    {
        validator: isValidJSON,
        error: "Malformed JSON",
    },
]

export const extraFeaturesValidator = [
    {
        validator: (extraFeatures: string) => !!extraFeatures,
        error: "Extra features is invalid",
    },
]

export const nameValidators = [
    {
        validator: (name: string) => name.length > 0,
        error: "Name must not be empty",
    },
    {
        validator: (name: string) => name.length <= 200,
        error: "Name cannot be greater than 200 characters",
    },
]

export const booleanValidator = (type: string) => [
    {
        validator: (bool: boolean) => bool === !!bool,
        error: type + " is not a boolean",
    },
]
export const longitudeValidators = [
    {
        validator: isValidLongitude,
        error: "Longitude is invalid",
    },
]
export const latitudeValidators = [
    {
        validator: isValidLatitude,
        error: "Latitude is invalid",
    },
]
export const blankValidator = [
    {
        validator: () => true,
        error: "",
    },
]
export const coordinatesValidators = [
    {
        validator: (coordinates: Coordinate[]) => coordinates.every(isValidLonLat),
        error: "Coordinates contain invalid values",
    },
]
export const boundingBoxValidators = [
    {
        validator: (bbox: Coordinate[]) => bbox.length === 4,
        error: "Bounding box has incorrect amount of coordinates",
    },
    {
        validator: () => {
            // check that it goes top-left -> top-right -> bottom-right -> bottom-left
            return true
        },
        error: "Bounding box is in the wrong order",
    },
    ...coordinatesValidators,
]

export const colorValidators = [
    {
        validator: (color: number) => color <= 4294967295 && color >= -2147483648,
        error: "Color is invalid size",
    },
    {
        validator: (color: number) => {
            color = color >>> 0
            if ((color & 0xff) === 0 && color & 0xffffff00) {
                return false
            }
            return true
        },
        error: "Color cannot have 0 alpha with color data",
    },
]

export const strokeValidators = [
    {
        validator: (stroke: number) => stroke >= 1 && stroke <= 4,
        error: "Stroke value must be between 1 and 4",
    },
]

export const enumValidator = (type: string, enumObj: Record<number, string>) => [
    {
        validator: (val: number) => !!enumObj[val],
        error: "Val is not a valid member of enum " + type,
    },
]

export const idValidators = [
    {
        validator: (id: string) => !!id,
        error: "ID is invalid",
    },
]

export const demoIdValidators = [
    {
        validator: (demoId: string) => Object.values(sampleDataIds.flightIds).includes(demoId),
        error: "supplied demoId does not exist in list of valid demoIds",
    },
]

export const indexValidators = [
    {
        validator: (index: string, layer: SortableLayer, project: Project) => {
            const layers: Layer[] = Object.entries(project)
                .filter(([type]) => !ignoredProjectKeys.includes(type))
                .flatMap(([, layer]: [string, Layer]) => layer)

            const siblings = layers.filter(
                (potentialSibling) =>
                    potentialSibling.parentId === layer.parentId && potentialSibling.id !== layer.id
            )

            return !siblings.some((sibling) => sibling.index === index)
        },
        error: "Index is invalid: Index is identical to some sibling's index",
    },
]

export const parentIdValidators = [
    {
        validator: (parentId: string, layer: SortableLayer, project: Project) => {
            const layerById: Record<string, Layer> = Object.entries(project)
                .filter(([type]) => !ignoredProjectKeys.includes(type))
                .reduce(
                    (acc, [, layers]: [string, Layer[]]) => {
                        layers.forEach((layer) => (acc[layer.id] = layer))
                        return acc
                    },
                    {} as Record<string, Layer>
                )

            return layerById[parentId]?.parentId !== layer.id
        },
        error: "Parent ID is invalid: Will create circulation",
    },
]

export const createdByValidators = [
    {
        validator: (createdBy: Author) => createdBy.userId.length === 36,
        error: "Author must include the user's userId",
    },
]

export const genericCreateLayerValidator = entityValidator(GenericLayer, {
    name: {
        validators: nameValidators,
        nonNull: true,
    },
    id: {
        validators: idValidators,
        nonNull: true,
    },
    createdBy: {
        validators: createdByValidators,
        nonNull: true,
    },
})

export const genericUpdateLayerValidator = entityValidator(Layer, {
    name: {
        validators: nameValidators,
        nonNull: true,
    },
    index: { validators: indexValidators, nonNull: true },
    parentId: { validators: parentIdValidators },
    visible: { validators: booleanValidator("visible"), nonNull: true },
    locked: { validators: booleanValidator("locked") },
})

export const createLayerValidator = <T, V extends Validator<ProtoType<T>>>(
    _: T,
    validation: { [K in keyof V]: K extends keyof ProtoType<T> ? V[K] : never }
) => {
    return { ...genericCreateLayerValidator, ...validation } as const
}

export const updateLayerValidator = <T, V extends Validator<ProtoType<T>>>(
    _: T,
    validation: { [K in keyof V]: K extends keyof ProtoType<T> ? V[K] : never }
) => {
    return { ...genericUpdateLayerValidator, ...validation } as const
}
